<template>
  <div class="setup_step card">
    <h4>Set up your custom email address</h4>
    <p class="step-description">
      Next, you need to set up an email address that all platform emails will sent from. Most customers choose an email
      on their agency website domain. For example, platform@yourdomain.com or seo@yourdomain.com.
    </p>
    <p class="step-description">
      You also need to add a ‘From Name’. This is what will is displayed within the ‘From’ field when your customers
      receive emails from the platform. For example, ‘My Agency SEO Platform.’
    </p>
    <p class="step-description">
      Click the button below to discover the steps you need to take to set this up.
    </p>

    <base-modal
      v-if="showInstructionsModal"
      @close="showInstructionsModal = false;"
    >
      <template #heading>
        Setup your custom email in 3 easy steps
      </template>
      <h3>1. Choose an email address</h3>
      <p>
        Firstly you need to select the email address you want us to use when sending emails on your behalf. You will
        need to have access to this email and be able to receive emails to it.
      </p>
      <hr class="xs-mt-20 xs-mb-20">
      <h3>2. Verify your email</h3>
      <p>
        Once you have entered your email address you will be sent an email from Postmark Support
        (support@postmarkapp.com) asking you to confirm sender signature. All you need to do here is click
        ‘Confirm Sender Signature’.
      </p>
      <p><em>Not received an email? Click resend confirmation and we will resend the confirmation email to you.</em></p>
      <hr class="xs-mt-20 xs-mb-20">
      <h3>3. Refresh this page!</h3>
      <p>
        Once you have verified your email simply <a href="javascript:window.location.reload();">refresh this page</a> and we will check
        that the email address is verified. After that any emails sent from the platform will use your email address.
      </p>
    </base-modal>

    <button
      class="button info"
      @click="showInstructionsModal = true;"
    >
      <svg-vue icon="icons/question_mark_in_circle" />
      How to set up your custom email address?
    </button>

    <div class="form-group">
      <label>Custom Email</label>
      <input
        v-model="customEmail"
        type="text"
        :class="[
          'form-control',
          errors.custom_email ? 'error' : '',
        ]"
        placeholder="Enter your email address"
      >
      <!-- TODO: Componentise? -->
      <div
        v-if="errors?.custom_email"
        class="text-danger"
      >
        <ul v-if="errors.custom_email.length > 1">
          <li
            v-for="(errorMessage, index) in errors.custom_email"
            :key="index"
            v-html="errorMessage"
          />
        </ul>
        <small
          v-else
          class="text-danger"
          v-html="errors.custom_email[0]"
        />
      </div>
      <small>
        Enter the email address you want us to use when sending emails on your behalf.
        For example, email@yourdomain.com.
      </small>
    </div>

    <div class="form-group">
      <label>From Name</label>
      <input
        v-model="fromName"
        type="text"
        :class="[
          'form-control',
          errors.from_name ? 'error' : '',
        ]"
        placeholder="Enter your from name"
      >
      <!-- TODO: Componentise? -->
      <div
        v-if="errors?.from_name"
        class="text-danger"
      >
        <ul v-if="errors.from_name.length > 1">
          <li
            v-for="(errorMessage, index) in errors.from_name"
            :key="index"
            v-html="errorMessage"
          />
        </ul>
        <small
          v-else
          class="text-danger"
          v-html="errors.from_name[0]"
        />
      </div>
      <small>
        This is what will be displayed within the “From” field when your customers receive emails from the platform.
        For example, ‘My Agency SEO Platform.’
      </small>
    </div>

    <button
      class="button"
      :disabled="loading"
      @click="submitCustomEmail"
    >
      <i
        v-if="loading"
        class="fas fa-spin fa-spinner"
      />
      Continue
    </button>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import axios from 'axios';
import Swal from 'sweetalert2';
import BaseModal from "../../global/modals/BaseModal";

export default {
  components: {
    BaseModal,
  },
  data() {
    return {
      showInstructionsModal: false,
      errors: {},
    };
  },
  computed: {
    ...mapGetters('merchantCentreSetupWizard', [
      'formFields',
      'loading',
    ]),
    customEmail: {
      get() {
        return this.formFields.customEmail;
      },
      set(customEmail) {
        this.setFormField({ customEmail });
      }
    },
    fromName: {
      get() {
        return this.formFields.fromName;
      },
      set(fromName) {
        this.setFormField({ fromName });
      }
    },
  },
  async mounted() {
    await this.fetchWhitelabelStatus();
  },
  methods: {
    ...mapActions('merchantCentreSetupWizard', [
      'setCurrentStep',
      'setFormField',
      'fetchWhitelabelStatus',
      'setLoadingState',
    ]),
    async submitCustomEmail() {
      this.setLoadingState(true);
      await axios.post('/merchant/setup-custom-email', {
          custom_email: this.customEmail,
          from_name: this.fromName,
        })
        .then(() => {
          Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: 'Custom email setup, please check your inbox for a verification email.',
            allowOutsideClick: false,
            allowEscapeKey: false,
          });

          this.setCurrentStep('CUSTOM_EMAIL');
        })
        .catch(
          (e) => this.$axiosExceptionHandler(e, ({ response }) => {
            if (response.status === 422) {
              const {errors} = response.data;
              this.errors = errors;
            } else {
              const {data} = response;
              Swal.fire({
                title: "Whoops",
                html: data.message ?? 'Something went wrong, please try again later.',
                icon: 'warning',
              });
            }
          }
        ))
        .finally(() => {
          this.setLoadingState(false);
        })
    },
  }
}
</script>

<style lang="scss" scoped>
  .button {
    margin-top: 1rem;
  }
</style>
